import React, { useEffect, useRef } from 'react';
import Player from '@vimeo/player';


const withEndScreenParams = (url) => {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}badge=0&byline=0&portrait=0&title=0&dnt=1`;
};

/**
 * VimeoPlayer wraps the Vimeo Player SDK.
 * To bypass Vimeo's end screen we poll the current time and reset the
 * video to the start (and pause) well before it reaches the end.
 */
const VimeoPlayer = ({ src, title, videoKey }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const durationRef = useRef(0);
  const resettingRef = useRef(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !src) return;

    if (playerRef.current) {
      playerRef.current.destroy().catch(() => {});
      playerRef.current = null;
    }

    const player = new Player(containerRef.current, {
      url: withEndScreenParams(src),
      responsive: false,
      autoplay: false,
    });

    playerRef.current = player;
    durationRef.current = 0;
    resettingRef.current = false;

    player
      .getDuration()
      .then((d) => {
        durationRef.current = d;
      })
      .catch(() => {});

    const resetToStart = () => {
      if (resettingRef.current) return;
      resettingRef.current = true;
      player
        .pause()
        .then(() => player.setCurrentTime(0))
        .catch(() => {})
        .finally(() => {
          setTimeout(() => {
            resettingRef.current = false;
          }, 500);
        });
    };

    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(async () => {
        try {
          const duration = durationRef.current || (await player.getDuration());
          durationRef.current = duration;
          if (!duration) return;

          const current = await player.getCurrentTime();
          const buffer = 0.5;

          if (current >= duration - buffer) {
            resetToStart();
          }
        } catch {
          /* player may be destroyed */
        }
      }, 100);
    };

    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const handlePlay = () => startPolling();
    const handlePause = () => {
      if (!resettingRef.current) stopPolling();
    };
    const handleEnded = () => resetToStart();

    player.on('play', handlePlay);
    player.on('pause', handlePause);
    player.on('ended', handleEnded);

    return () => {
      stopPolling();
      player.off('play', handlePlay);
      player.off('pause', handlePause);
      player.off('ended', handleEnded);
      player.destroy().catch(() => {});
      playerRef.current = null;
    };
  }, [src, videoKey]);

  return <div ref={containerRef} title={title} className="vimeo-player" />;
};

export default VimeoPlayer;